/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import groupBy from 'lodash/groupBy';
import isNil from 'lodash/isNil';

// TODO Podpora pro možnost volby počátečního dne v týdnu (WKST)
// - V DHTMLX se nastavuje v konfiguraci jako start_on_monday.
//   Dále pak je tu nastavení recurring_workdays, ale to asi vliv nemá.
// - Na druhou stranu toto nastavení může ovlivnit výsledek
//   (viz. příklad "days generated makes a difference" ve specifikaci).
//   This is significant when a WEEKLY "RRULE" has an interval greater than 1,
//   and a BYDAY rule part is specified.
// - The default value is MO. => takže nyní není kritické.
// - Zvážit ale přidat jako volitelnou konfiguraci,
//   která při MO do ics nevypíše nic (kompatibilita, menší data).
// TODO Podpora pro DHTMLX události bez recurring extension:
// - Přináší možnost nevyplnění hodnot bez recurring extension.
//   V podstatě asi půjde o to počítat nejen s "" ale i s undefined.
// - Nebo používat výchozí hodnoty a pak vždy spoléhat, že jsou vyplněné?
// TODO Podpora pro časové zóny, ne jen pouze floating TZ.
// - Toto nastavení v DHTMLX skutečně existuje, ale na serveru se nepoužívá.
//   Pro náš případ tedy zatím zbytečné.
//   Pro univerzální převodník DHTMLX2iCal by asi mělo být podporováno.
// - server_utc, occurrence_timestamp_in_utc
// TODO Podpora pro různé datové typy v DHTMLX událostech.
// - Dle dokumentace to vypadá, že některé typ mohou být kromě string,
//   také typu number konkrétně: id, event_pid a event_length.
// - Změnu je nutné promítnout do DhtmlxEvent.
// - Zvážit i změnu TrackPlanCommunicator, kde by se čísla zbytečně nepřeváděli
//   na string a pak při exportu zase zpět.
// - https://docs.dhtmlx.com/scheduler/loading_data.html#databasestructure
// TODO Podpora pro nastavení include_end_by.
// - Není úplně jisté, jestli má nějaký vliv na formát dat.
// - https://docs.dhtmlx.com/scheduler/api__scheduler_include_end_by_config.html
// TODO Podpora pro vyjádření konce události definicí délky jejího trvání.
// - Možná přidat konfiguraci číselníkem:
//   - "EndDate" by fungovalo jako nyní.
//   - "Duration" by vždy přepočítávalo na DURATION.
//     Když je definován length, tak přehrat hodnotu z něj (mimo výjimek).
//     Jinak doby vypočítat z rozdílu DTSTART a DTEND.
//   - "Auto" by používalo odlišné způsoby u odlišných typů události dle DHTMLX.
//     U rekurzivních událostí použít DURATION.
//     U obyčejných událostí a u vyjímek použít DTEND.
// - Výhody:
//   - Pokud je DURATION 1 den, není třeba uvádět => menší výstup.
//   - Jednodušší výpočet bez nutnosti převodu na UNIX time.
//   - Zachování délky při změny DTSTART
//     (DTEND je třeba přepočítávat).
// - Nevýhody:
//   - V DHTMLX je event_length je definovaný pouze u recurring expansion!
//     Tzn. buď podporovat pouze tento formát, anebo se bude muset řešit
//     fallback na parseRecurringEndDate.
//     Nejlepším řešením asi bude vyžadovat recurring format
//     a pokud by měkteré položky chyběli, tak je v komunikátoru přidat.
// TODO Nevalidní iCalendar formát u změněných instancí opakování:
// - Chybové hlášení: UID value is not unique, duplicate found
// - Lze použít *.ics z test38 - test42, které chybu ve validátoru vyvolají.
// - S největší pravděpodobností jde o bug validátoru.
//   Takto to totiž dělá nejen DHTMLX PHP, Mozilla, ale dokonce i specifikace.
// - Bylo reportováno https://gitlab.ki-wi.cz/light/player-lighter/-/issues/373#note_77634
// - https://tools.ietf.org/html/rfc5546#section-4.4.8
// - https://tools.ietf.org/html/rfc5545#section-3.8.4.4
// - https://tools.ietf.org/html/rfc5545#section-3.8.4.7
/**
 * Exportér z formátu DHTMLX Scheduler do formátu iCalendar
 *
 * Byl vytvořen na základě transpilace oficiálního exportéru v PHP do JS
 * a následné optimalizace.
 * Základem byl DHTMLX Scheduler verze 4.0.1, který se používá na serveru.
 *
 * Zdroje:
 * - [RFC 5545](https://tools.ietf.org/html/rfc5545)
 * - [Creating basic iCalendar](https://github.com/mozilla-comm/ical.js/wiki/Creating-basic-iCalendar)
 * - [DHTMLX iCal export/import](https://docs.dhtmlx.com/scheduler/ical_export_import.html)
 */
class DhtmlxToIcalExporter {
  /**
   * @param {IcalApi} ICAL
   * @param {DhtmlxToIcalEventFactory} eventFactory
   * @param {DhtmlxToIcalRecTypeFactory} recTypeFactory
   * @param {IcalTimeUtil} timeUtil
   * @param {IcalUtil} util
   */
  constructor(ICAL, eventFactory, recTypeFactory, timeUtil, util) {
    this.ICAL = ICAL;
    this.eventFactory = eventFactory;
    this.recTypeFactory = recTypeFactory;
    this.timeUtil = timeUtil;
    this.util = util;
  }

  /**
   * Exportuje DHTMLX Scheduler události do iCalendar.
   * @param {Array.<DhtmlxEvent>} dhtmlxEvents DHTMLX události
   * @param {Object} [opts={}]
   * @param {string} [opts.dateFormat] Formát datumů v DHTMLX událostech
   * @param {DhtmlxToIcalExporter~generateUid} [opts.generateUid]
   * Callback, kterým se generuje UID pro iCalendar událost.
   * Bez zadání se použije identifikátor DHTMLX události.
   * @param {Date} [opts.nowDate] Aktuální datum pro vytvoření časového otisku
   * @returns {string|undefined} Exportovaný kalendář ve formátu iCalendar,
   * nebo `undefined` pokud vstup neobsahoval žádné události.
   * To je z důvodu, že iCalendar formát vyžaduje přítomnost více jak jedné
   * události v celém kalendáři, ale počet DHTMLX událostí omezený není.
   */
  export(
    dhtmlxEvents,
    {
      dateFormat = '%Y-%m-%d %H:%i',
      generateUid = (dhtmlxEvent) => dhtmlxEvent.id,
      nowDate = new Date()
    } = {}
  ) {
    // Vytvoří se pomocné třídy pro převod mezi formáty událostí.
    const dhtmlxToIcalEvents = dhtmlxEvents.map((dhtmlxEvent) =>
      this.eventFactory.create(dateFormat, dhtmlxEvent)
    );
    // Události se pro optimalizaci výkonu v jednom průchodu rozdělí dle jejich
    // event_pid, které určuje, jestli jde o rodičovskou událost,
    // nebo o událost potomka.
    // TODO Události, které nemají rodičovskou, jsou potichu zahozené.
    // - Místo toho raději vyhazovat chybu, nebo je přidat i tak?
    const eventGroups = groupBy(dhtmlxToIcalEvents, (dhtmlxToIcalEvent) =>
      dhtmlxToIcalEvent.getParentId()
    );
    // Určí se seznam rodičovských událostí, které nemají žádné event_pid.
    const parentEvents = eventGroups[''] || [];
    // Bez událostí nelze vytvořit validní kaledář.
    if (!parentEvents.length) {
      return;
    }
    // Čas vytvoření se převede na iCalendar formát.
    // Používá se pro synchronizaci změn v kalendáři pomocí DTSTAMP.
    // TODO Hard-coded deaktivace timezone.
    const nowIcalDate = this.ICAL.Time.fromJSDate(nowDate, true);
    // Vytvoří se nový kalendář pomocí API.
    const vcalendar = this.util.createVCalendar({
      // Jednotlivé skupiny DHTMLX událostí (rodič + jeho děti) se transformují
      // na iCalendar události a spojí se do jednoho výsledného seznamu.
      subcomponents: parentEvents.reduce((acc, event) => {
        const id = event.getId();
        const childEvents = eventGroups[id] || [];
        const dhtmlxEvent = event.getEvent();
        const uid = generateUid(dhtmlxEvent);
        const vevents = this.parseEvents(event, childEvents, nowIcalDate, uid);
        acc.push(...vevents);
        return acc;
      }, [])
    });
    // Objekt kalendáře se převede na řetězec dle RFC 5545.
    return vcalendar.toString();
  }

  /**
   * Načte skupinu souvisejících DHTMLX Scheduler událostí.
   * Skupina je tvořena jednou rodičovskou událostí a libovolným počtem událostí
   * potomků, které na rodičovskou událost odkazují.
   * Události potomků se používají pouze u opakovaných událostí a dělí se
   * na dvě skupiny: zrušení a změna jedné instance opakování.
   * @param {DhtmlxToIcalEvent} parentEvent Rodičovská událost
   * @param {Array.<DhtmlxToIcalEvent>} childEvents Události potomků
   * @param {IcalApi.Time} nowDate Aktuální datum pro vytvoření časového otisku
   * @param {string} uid
   * @private
   * @returns {Array.<IcalApi.Component>}
   */
  parseEvents(parentEvent, childEvents, nowDate, uid) {
    // Pokud nemá DHTMLX událost rec_type, tak jde o událost bez opakování.
    // TODO Události, které mají rodičovskou událost bez rec_type, jsou potichu zahozené.
    // - Místo toho raději vyhazovat chybu, nebo je přidat i tak?
    if (parentEvent.getRecType() === '') {
      return [this.parseOneTimeEvent(parentEvent, nowDate, uid)];
    }
    // Události potomků se pro optimalizaci výkonu v jednom průchodu rozdělí
    // dle jejich rec_type, které určuje, jestli jde o zrušení nebo změnu
    // jedné instance opakování.
    const eventsGroup = groupBy(childEvents, (childEvent) =>
      childEvent.getRecType()
    );
    const excludedEvents = eventsGroup.none || [];
    const updatedEvents = eventsGroup[''] || [];
    // Výsledky všech typů DHTMLX událostí se sloučí do jednoho seznamu
    // iCalendar událostí.
    // V iCalendar formátu se zrušení instancí opakování vkládá do události
    // s opakování, takže je několik DHTMLX událostí převedeno na jednu
    // iCalendar událost.
    return [
      this.parseRecurringEvent(parentEvent, excludedEvents, nowDate, uid),
      ...updatedEvents.map((updatedEvent) =>
        this.parseExceptionEvent(updatedEvent, nowDate, uid)
      )
    ];
  }

  /**
   * Načte DHTMLX Scheduler událost se změnou jedné instance opakování.
   * @param {DhtmlxToIcalEvent} event Událost
   * @param {IcalApi.Time} nowDate Aktuální datum pro vytvoření časového otisku
   * @param {string} uid
   * @private
   * @returns {IcalApi.Component}
   */
  parseExceptionEvent(event, nowDate, uid) {
    return this.util.createVEvent({
      endDate: event.getEndDate(),
      // Odkaz na měněnou instanci opakování je překvapivě uložen jako
      // "event_length", kde je uložen UNIX čas [s] začátku instance.
      // The "RECURRENCE-ID" property allows the reference to an individual
      // instance within the recurrence set.
      recurrenceDate: this.timeUtil.fromUnixTime(event.getLength(), {
        zone: this.ICAL.Timezone.localTimezone
      }),
      stamp: nowDate,
      startDate: event.getStartDate(),
      summary: event.getText(),
      // The full range of calendar components specified by a
      // recurrence set is referenced by referring to just the "UID"
      // property value corresponding to the calendar component.
      uid
    });
  }

  /**
   * Načte DHTMLX Scheduler jednorázovou událost bez opakování.
   * @param {DhtmlxToIcalEvent} event Událost
   * @param {IcalApi.Time} nowDate Aktuální datum pro vytvoření časového otisku
   * @param {string} uid
   * @private
   * @returns {IcalApi.Component}
   */
  parseOneTimeEvent(event, nowDate, uid) {
    return this.util.createVEvent({
      endDate: event.getEndDate(),
      stamp: nowDate,
      startDate: event.getStartDate(),
      summary: event.getText(),
      uid
    });
  }

  /**
   * Načte datum konce opakované události.
   *
   * V iCalendar formátu má být u opakované událost datum konce první instance.
   * V DHTML formátu je tam naopak datum konce poslední instance.
   * Proto je nutné převést přepočet.
   *
   * Jsou zde 2 způsoby jak definovat konec události:
   * - definováním data ukončení události (`end_date`, `DTEND`)
   * - definováním doby trvání události (`event_length`, `DURATION`)
   *
   * iCalendar formát dovoluje použít libovolný způsob, ale vždy právě jeden.
   * DHTMLX formát někdy informace duplikuje, někdy není dostupné
   * `end_date` (u opakovaných událostí) a jindy zase není dostupná
   * `event_length` (bez recurring extension).
   *
   * Pro jednotný přístup ke všem událostem a shodně s oficiálním PHP exportérem
   * a GUI kalendářem bylo provedeno rozhodnutí, že se konec události bude
   * definovat pomocí data ukončení.
   *
   * Zdroje:
   * - [DHTMLX auto_end_date](https://docs.dhtmlx.com/scheduler/api__scheduler_auto_end_date_config.html)
   * @param {number} length
   * @param {IcalApi.Time} startDate
   * @private
   * @returns {IcalApi.Time}
   */
  parseRecurringEndDate(length, startDate) {
    return this.timeUtil.adjust(startDate, { seconds: length });
  }

  /**
   * Načte DHTMLX Scheduler událost s opakováním.
   * @param {DhtmlxToIcalEvent} parentEvent
   * @param {Array.<DhtmlxToIcalEvent>} excludedEvents
   * @param {IcalApi.Time} nowDate Aktuální datum pro vytvoření časového otisku
   * @param {string} uid
   * @private
   * @returns {IcalApi.Component}
   */
  parseRecurringEvent(parentEvent, excludedEvents, nowDate, uid) {
    // Vytvoří se pomocná třída pro převod mezi formáty opakování.
    const recType = this.recTypeFactory.create(parentEvent);
    const recurStartDate = parentEvent.getStartDate();
    const length = parentEvent.getLength();
    const recur = recType.getRecur();
    // Přepočet data začátku dle odlišností iCalendar formátu
    const firstStartDate = this.parseRecurringStartDate(recur, recurStartDate);
    return this.util.createVEvent({
      // Přepočet data konce dle odlišností iCalendar formátu
      endDate: this.parseRecurringEndDate(length, firstStartDate),
      // DHTMLX události zrušení instance opakování se transformují na EXDATE.
      // Odkaz na měněnou instanci opakování je překvapivě uložen jako
      // "event_length", kde je uložen UNIX čas [s] začátku instance.
      excludedDates: excludedEvents.map((event) =>
        this.timeUtil.fromUnixTime(event.getLength(), {
          zone: this.ICAL.Timezone.localTimezone
        })
      ),
      // Transformovaná konfigurace opakování
      recur,
      stamp: nowDate,
      startDate: firstStartDate,
      summary: parentEvent.getText(),
      uid
    });
  }

  /**
   * Načte datum začátku opakované události.
   *
   * iCalendar formát vyžaduje, aby bylo u opakované události nastaveno
   * na datum první instance.
   * DHTMLX formát pořadavky specifikuje podobně, ale ve skutečnosti se mnohdy
   * objeví datum, na které uživatel kliknul, než datum začátku první instance.
   * Proto je nutné převést přepočet tak, jak to dělá i oficiální PHP exportér.
   * Narozdíl od něj, je zde ale k výpočtu použito iCal API Recur.
   * @param {IcalApi.Recur} recur
   * @param {IcalApi.Time} recurStartDate
   * @private
   * @returns {IcalApi.Time}
   */
  parseRecurringStartDate(recur, recurStartDate) {
    // Pomocí iterátoru se nalezne první výskyt události od začátku opakování.
    const iterator = recur.iterator(recurStartDate);
    const firstDate = iterator.next();
    // Při nenalezení prvního výskytu se použije originální datum začátku.
    if (isNil(firstDate)) {
      return recurStartDate;
    }
    // Vrácení datumu prvního výskytu.
    return firstDate;
  }
}

/**
 * Vytvoří unikátní identifikátor iCalendar události.
 * Identifikátor by měl splňovat požadavky z iCalendar specifikace,
 * mezi nejdůležitější patří:
 * - The "UID" itself MUST be a globally unique identifier.
 * - The generator of the identifier MUST guarantee that the identifier
 *   is unique.
 * - "UID" values MUST NOT include any data that might identify a user, host,
 *   domain, or any other security- or privacy-sensitive information.
 * - "UID" value MUST have a length less than 255 octets and MUST conform
 *   to the "iana-token" ABNF syntax
 * @callback DhtmlxToIcalExporter~generateUid
 * @param {DhtmlxEvent} dhtmlxEvent DHTMLX událost, která se převádí
 * na iCalendar událost a jejíž identifikátor se generuje.
 * @returns {string}
 */

export default DhtmlxToIcalExporter;
